import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Ticket, CalendarX, AlertTriangle, XCircle, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_971b0a5e-2a58-4bd4-a5ef-b384a7e4c355/artifacts/qroeuqc4_D09061CF-773C-466E-9659-A1C4CF9EC193.png";

const STATUS = {
  pending: { label: "Aguardando confirmação", cls: "text-gold border-gold/60" },
  confirmed: { label: "Confirmado", cls: "text-green-400 border-eragreen" },
  cancelled: { label: "Cancelado", cls: "text-smoke border-white/15" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
};

export default function Lookup() {
  const [code, setCode] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setConfirming(false);
    try {
      const { data } = await axios.get(`${API}/appointments/lookup`, { params: { code, whatsapp } });
      setResult(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro na consulta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/appointments/cancel`, { code, whatsapp });
      setResult(data);
      setConfirming(false);
      toast.success("Agendamento cancelado. O horário foi liberado.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao cancelar.");
    } finally {
      setCancelling(false);
    }
  };

  const submitReview = async () => {
    if (rating === 0) return;
    setReviewing(true);
    try {
      await axios.post(`${API}/reviews`, { code, whatsapp, rating, comment });
      setReviewed(true);
      toast.success("Avaliação enviada. Valeu!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao enviar avaliação.");
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="lookup-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="lookup-home-link">
            <img src={LOGO} alt="Estúdio New Era" className="h-9 w-9 md:h-11 md:w-11 rounded-full object-cover ring-1 ring-gold/50" />
            <span className="font-bebas text-lg md:text-xl tracking-widest text-cream">Estúdio <span className="text-gold">New Era</span></span>
          </Link>
          <Link to="/" className="focus-gold flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-smoke hover:text-gold transition-colors" data-testid="lookup-back-home">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="font-playfair italic text-gold text-2xl md:text-3xl">seu horário</p>
          <h1 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl mt-1">Consultar agendamento</h1>
          <p className="text-sm text-smoke mt-4">Digite o código que você recebeu ao agendar e o mesmo WhatsApp usado no cadastro.</p>
        </motion.div>

        <form onSubmit={search} className="mt-10 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="lookup-form">
          <div>
            <label htmlFor="lookup-code" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Código</label>
            <input
              id="lookup-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ex.: F6DF25"
              maxLength={8}
              required
              data-testid="lookup-code-input"
              className="focus-gold w-full bg-surface border border-white/15 text-cream font-bebas text-2xl tracking-[0.3em] px-5 py-3.5 outline-none placeholder:text-smoke/40 uppercase"
            />
          </div>
          <div>
            <label htmlFor="lookup-whatsapp" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">WhatsApp</label>
            <input
              id="lookup-whatsapp"
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="(47) 99999-9999"
              required
              data-testid="lookup-whatsapp-input"
              className="focus-gold w-full bg-surface border border-white/15 text-cream font-manrope px-5 py-4 outline-none placeholder:text-smoke/50"
            />
          </div>
          <button
            type="submit"
            disabled={loading || code.trim().length < 4}
            data-testid="lookup-search-button"
            className="focus-gold inline-flex items-center justify-center gap-2 bg-gold text-ink font-bebas text-xl tracking-widest px-8 py-3.5 hover:bg-golddark transition-colors disabled:opacity-40"
          >
            <Search className="w-5 h-5" /> {loading ? "Buscando..." : "Consultar"}
          </button>
        </form>

        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 border border-white/10 bg-surface p-8 md:p-10"
              data-testid="lookup-result"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-smoke flex items-center gap-2"><Ticket className="w-4 h-4 text-gold" /> Código {result.code}</p>
                  <h2 className="font-bebas text-4xl text-cream mt-3">{result.service_name} <span className="text-gold">· R$ {result.price}</span></h2>
                  <p className="text-sm text-smoke mt-2 capitalize">com {result.barber_name} · {fmtDate(result.date)} às {result.time}</p>
                  <p className="text-sm text-smoke">Cliente: {result.name}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 ${STATUS[result.status]?.cls || ""}`} data-testid="lookup-status">
                  {STATUS[result.status]?.label || result.status}
                </span>
              </div>

              {result.status !== "cancelled" && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  {confirming ? (
                    <div className="flex flex-wrap items-center gap-4" data-testid="lookup-confirm-cancel">
                      <p className="text-sm text-eraorange flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Tem certeza? Essa ação não pode ser desfeita.</p>
                      <button
                        onClick={cancel}
                        disabled={cancelling}
                        data-testid="lookup-confirm-yes-button"
                        className="focus-gold bg-red-600 text-cream font-bebas text-lg tracking-widest px-6 py-2 hover:bg-red-700 transition-colors disabled:opacity-40"
                      >
                        {cancelling ? "Cancelando..." : "Sim, cancelar"}
                      </button>
                      <button
                        onClick={() => setConfirming(false)}
                        data-testid="lookup-confirm-no-button"
                        className="focus-gold border border-white/20 text-cream font-bebas text-lg tracking-widest px-6 py-2 hover:border-gold hover:text-gold transition-colors"
                      >
                        Manter horário
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(true)}
                      data-testid="lookup-cancel-button"
                      className="focus-gold inline-flex items-center gap-2 border border-white/15 text-smoke font-bebas text-lg tracking-widest px-6 py-2.5 hover:border-red-500 hover:text-red-400 transition-colors"
                    >
                      <CalendarX className="w-4 h-4" /> Cancelar agendamento
                    </button>
                  )}
                </div>
              )}

              {result.status === "cancelled" && (
                <p className="mt-8 pt-6 border-t border-white/10 text-sm text-smoke flex items-center gap-2" data-testid="lookup-cancelled-note">
                  <XCircle className="w-4 h-4 text-eraorange" /> Este horário foi cancelado e já está livre para outro cliente.
                </p>
              )}

              {result.can_review && !reviewed && (
                <div className="mt-8 pt-6 border-t border-white/10" data-testid="review-form">
                  <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-4">Como foi o atendimento? Avalie:</p>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        aria-label={`${n} estrelas`}
                        data-testid={`review-star-${n}`}
                        className="focus-gold p-1"
                      >
                        <Star className={`w-8 h-8 transition-colors ${n <= rating ? "text-gold fill-gold" : "text-smoke/40 hover:text-gold/60"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Conte como ficou o corte (opcional)"
                    maxLength={300}
                    rows={3}
                    data-testid="review-comment-input"
                    className="focus-gold mt-4 w-full bg-ink border border-white/15 text-cream font-manrope px-4 py-3 text-sm outline-none placeholder:text-smoke/50 resize-none"
                  />
                  <button
                    onClick={submitReview}
                    disabled={rating === 0 || reviewing}
                    data-testid="review-submit-button"
                    className="focus-gold mt-4 bg-gold text-ink font-bebas text-lg tracking-widest px-8 py-2.5 hover:bg-golddark transition-colors disabled:opacity-40"
                  >
                    {reviewing ? "Enviando..." : "Enviar avaliação"}
                  </button>
                </div>
              )}
              {reviewed && (
                <p className="mt-8 pt-6 border-t border-white/10 text-sm text-gold flex items-center gap-2" data-testid="review-thanks">
                  <Star className="w-4 h-4 fill-gold" /> Valeu pela avaliação! Ela pode aparecer no site.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-10 text-center text-sm text-smoke">
          Ainda não tem horário?{" "}
          <Link to="/agendar" className="text-gold hover:text-golddark transition-colors" data-testid="lookup-booking-link">Agende agora</Link>
        </p>
      </main>
    </div>
  );
}
